/**
 * `Switch` y `Checkbox` con su etiqueta asociada, una sola vez.
 *
 * Existe por accesibilidad, no por comodidad. Radix monta ambos como
 * `<button role="switch">` / `<button role="checkbox">`, y **envolverlos en un
 * `<label>` no los asocia**: la asociación implícita del `<label>` solo aplica
 * a controles etiquetables por contenido. Sin `htmlFor` apuntando a un `id`
 * real, un lector de pantalla anuncia «botón, conmutador» sin decir de qué.
 *
 * Había una decena de estos repartidos por las vistas de administración; todos
 * habrían tenido que arreglarse uno a uno. Aquí se arregla una vez.
 */
import { useId } from 'react'
import type { ReactNode } from 'react'
import { Checkbox, Switch } from '@mpfn/ui'

interface PropsConmutador {
  etiqueta: ReactNode
  marcado: boolean
  onCambio: (marcado: boolean) => void
  /** Texto de apoyo bajo la etiqueta. */
  descripcion?: string
  deshabilitado?: boolean
}

export function Interruptor({
  etiqueta,
  marcado,
  onCambio,
  descripcion,
  deshabilitado = false,
}: PropsConmutador) {
  const id = useId()
  const idDescripcion = `${id}-descripcion`

  return (
    <div className="sigfisc-conmutador">
      <Switch
        id={id}
        checked={marcado}
        disabled={deshabilitado}
        onCheckedChange={onCambio}
        {...(descripcion !== undefined ? { 'aria-describedby': idDescripcion } : {})}
      />
      <span className="sigfisc-conmutador__texto">
        <label htmlFor={id}>{etiqueta}</label>
        {descripcion !== undefined && (
          <span id={idDescripcion} className="sigfisc-nota">
            {descripcion}
          </span>
        )}
      </span>
    </div>
  )
}

export function Casilla({
  etiqueta,
  marcado,
  onCambio,
  descripcion,
  deshabilitado = false,
}: PropsConmutador) {
  const id = useId()
  const idDescripcion = `${id}-descripcion`

  return (
    <div className="sigfisc-conmutador">
      <Checkbox
        id={id}
        checked={marcado}
        disabled={deshabilitado}
        onCheckedChange={(valor) => {
          // Radix admite el estado «indeterminado»; aquí no se usa, y tratarlo
          // como marcado sería mentir sobre lo que el usuario eligió.
          onCambio(valor === true)
        }}
        {...(descripcion !== undefined ? { 'aria-describedby': idDescripcion } : {})}
      />
      <span className="sigfisc-conmutador__texto">
        <label htmlFor={id}>{etiqueta}</label>
        {descripcion !== undefined && (
          <span id={idDescripcion} className="sigfisc-nota">
            {descripcion}
          </span>
        )}
      </span>
    </div>
  )
}
